import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Home, Loader2, CheckCircle2 } from 'lucide-react';

/**
 * Inline Rental Status toggle — collects "Is this a rental property?
 * Y/N" + tenant + agent contacts at booking time, BEFORE the roof
 * inspection. Persists to the property doc so the value flows through
 * to the Work Sheet PDF (Rental box under Roof Details) and the invoice
 * letter (Tenant/Agent block).
 *
 * Rendered at the top of the Job Tasks tab on AppraisalDetailPage.
 */
export default function RentalStatusInline({ api, propertyId, onChanged }) {
  const [prop, setProp] = useState(null);
  const [agents, setAgents] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!propertyId) return;
    (async () => {
      try {
        const [{ data: p }, { data: ags }] = await Promise.all([
          api.get(`/properties/${propertyId}`),
          api.get('/agents'),
        ]);
        setProp(p);
        setAgents(ags || []);
      } catch {
        setProp(null);
      }
    })();
  }, [api, propertyId]);

  if (!propertyId) return null;
  if (!prop) {
    return (
      <div data-testid="rental-status-loading" className="bg-white border border-[#E2E2DF] rounded-md p-4 text-[11px] text-slate-400 flex items-center gap-2">
        <Loader2 size={11} className="animate-spin" /> Loading property…
      </div>
    );
  }

  const patch = async (partial) => {
    setBusy(true);
    setProp(prev => ({ ...prev, ...partial }));
    try {
      const { data } = await api.patch(`/properties/${propertyId}`, partial);
      setProp(data);
      toast.success('Property updated');
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    }
    setBusy(false);
  };

  const isRental = !!prop.is_rental;

  return (
    <section data-testid="rental-status-inline" className="bg-white border border-[#E2E2DF] rounded-md p-4 space-y-3">
      <header className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Home size={14} className="text-[#D35400]" />
          <h3 className="text-sm font-bold text-slate-800">Rental status</h3>
          {isRental && <CheckCircle2 size={12} className="text-emerald-600" />}
        </div>
        <p className="text-[10px] text-slate-500">Captured at booking. Flows into the Work Sheet "Rental" box and the invoice letter.</p>
      </header>

      {/* Yes / No segmented pills */}
      <div className="flex items-center gap-2">
        <button
          data-testid="rental-yes-btn"
          disabled={busy}
          onClick={() => patch({ is_rental: true })}
          className={`px-3 py-1 text-xs font-bold rounded-md border transition-colors ${isRental
            ? 'bg-emerald-600 text-white border-emerald-600'
            : 'bg-white text-slate-600 border-slate-300 hover:border-emerald-300'}`}
        >
          Yes (Rental)
        </button>
        <button
          data-testid="rental-no-btn"
          disabled={busy}
          onClick={() => patch({ is_rental: false })}
          className={`px-3 py-1 text-xs font-bold rounded-md border transition-colors ${!isRental
            ? 'bg-slate-800 text-white border-slate-800'
            : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'}`}
        >
          No (Owner-occupied)
        </button>
        {busy && <Loader2 size={11} className="animate-spin text-slate-400" />}
      </div>

      {/* Conditional contact fields */}
      {isRental && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-slate-100">
          <Field label="Tenant Name">
            <input data-testid="rental-tenant-name" type="text" defaultValue={prop.tenant_contact_name || ''}
                   onBlur={e => e.target.value !== (prop.tenant_contact_name || '') && patch({ tenant_contact_name: e.target.value })}
                   className="w-full px-2 py-1 text-xs border border-slate-300 rounded" />
          </Field>
          <Field label="Tenant Phone">
            <input data-testid="rental-tenant-phone" type="text" defaultValue={prop.tenant_contact_number || ''}
                   onBlur={e => e.target.value !== (prop.tenant_contact_number || '') && patch({ tenant_contact_number: e.target.value })}
                   className="w-full px-2 py-1 text-xs border border-slate-300 rounded" />
          </Field>
          <Field label="Tenant Email">
            <input data-testid="rental-tenant-email" type="email" defaultValue={prop.tenant_email || ''}
                   onBlur={e => e.target.value !== (prop.tenant_email || '') && patch({ tenant_email: e.target.value })}
                   className="w-full px-2 py-1 text-xs border border-slate-300 rounded" />
          </Field>
          <Field label="Managing Agent">
            <select data-testid="rental-agent-select" defaultValue={prop.real_estate_agent_id || ''}
                    onChange={e => patch({ real_estate_agent_id: e.target.value || null })}
                    className="w-full px-2 py-1 text-xs border border-slate-300 rounded">
              <option value="">— No agent on file —</option>
              {agents.map(a => (
                <option key={a._id} value={a._id}>{a.name}{a.company ? ` · ${a.company}` : ''}</option>
              ))}
            </select>
          </Field>
        </div>
      )}
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-0.5">{label}</label>
      {children}
    </div>
  );
}
